import { DateType, Status, StatusDisplay } from '@/pages/GlicoUnitComsumption/utils';
import { keepFourOrLine, keepFourOrNull } from '@/utils/utils';
import { V2ProductionCostGetProductionCostPostResponse } from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import _, { isNil } from 'lodash';

export const getChartOption = (resolution?: DateType, chartData?: V2ProductionCostGetProductionCostPostResponse) => {
  const formatDate = resolution === DateType.MIN ? 'HH:mm' : resolution === DateType.DAY ? 'YYYY-MM-DD' : 'YYYY-MM';

  // 单耗
  let productionCostData = (chartData?.costDetailChartList ?? []).map(item => [
    dayjs(item.time).valueOf(),
    keepFourOrNull(item.productionCost),
    item.productionCostUnitName,
  ]);

  // 用能
  let useEnergyData = (chartData?.costDetailChartList ?? []).map(item => [
    dayjs(item.time).valueOf(),
    keepFourOrNull(item.totalCost),
    item.costUnitName,
  ]);

  // 用能最大最小与间隔
  const maxuseEnergyDataWithCalc = Math.max(...(chartData?.costDetailChartList ?? []).map(i => i.totalCost ?? 0));
  const minuseEnergyDataWithCalc = Math.min(...(chartData?.costDetailChartList ?? []).map(i => i.totalCost ?? 0));
  let maxUseEnergy = Math.ceil(Math.max(maxuseEnergyDataWithCalc) / 4) * 4;
  let minUseEnergy = Math.floor(Math.min(minuseEnergyDataWithCalc) / 4) * 4;
  const useEnergyInterval = Number(((maxUseEnergy - minUseEnergy) / 4).toFixed(0));

  // 产品
  const groupedByShowProductionId = _.groupBy(chartData?.costDetailChartList, 'showProductionId');
  const productionList = Object.values(groupedByShowProductionId);

  // 产出物图例颜色
  const productionChartsColors = [
    '#FFCB47',
    '#2D8DFF',
    '#FF477B',
    '#CE90D1',
    '#ABD335',
    '#FF9247',
    '#52E7FF',
    '#854ECA',
  ];

  let productionListData = productionList?.map((item, index) => {
    let data = item?.map(i => {
      return [dayjs(i.time).valueOf(), keepFourOrNull(i.production), i.productionUnitName, i.showProductionName];
    });
    return {
      name: item[0].showProductionName ?? '--',
      timeResolution: resolution,
      unit: item?.[0]?.productionUnitName,
      z: 1,
      type: 'bar',
      yAxisIndex: 1,
      data,
      itemStyle: {
        color: productionChartsColors[index % productionChartsColors.length], // 图形颜色
      },
    };
  });

  // 产出物最大最小与间隔
  const maxproductionListDataWithCalc = Math.max(...(chartData?.costDetailChartList ?? []).map(i => i.production ?? 0));
  const minproductionListDataWithCalc = Math.min(...(chartData?.costDetailChartList ?? []).map(i => i.production ?? 0));
  let productionMax = Math.ceil(Math.max(maxproductionListDataWithCalc) / 4) * 4;
  let productionMin = Math.floor(Math.min(minproductionListDataWithCalc) / 4) * 4;
  const productionInterVal = Number(((productionMax - productionMin) / 4).toFixed(0));

  const productionChartsColorMap = (productionListData ?? []).map((_, index) => {
    return productionChartsColors[index % productionChartsColors.length];
  });

  const commonOption = {
    grid: {
      left: 60,
      right: 40,
      top: 70,
      bottom: 70,
    },
    tooltip: {
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue).format(resolution === DateType.MIN ? 'MM-DD HH:mm' : formatDate);
        let otherStr = '';
        let str = '';

        items.forEach((item: any) => {
          const { seriesName, data } = item;
          let unit = data[2];
          otherStr += `<div style="margin-top:8px"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${
            seriesName === '单耗' ? 'transparent' : item.color
          };"></span>${seriesName} : &nbsp;${!isNil(data[1]) ? data[1] + unit : '-'}</div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },
    dataZoom: [
      {
        type: 'slider',
        bottom: 10,
        left: 25,
        right: 20,
        height: 20,
        textStyle: {
          fontSize: 10,
        },
        labelFormatter: (value: string) => {
          return dayjs(value).format(formatDate);
        },
      },
    ],
    xAxis: [
      {
        type: 'time',
        axisLabel: {
          formatter: function (e: number) {
            return dayjs(e, 'x').format(formatDate);
          },
        },
        axisPointer: {
          type: 'shadow',
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: chartData?.costDetailChartList?.[0]?.costUnitName,
        axisLabel: {
          formatter: '{value}',
        },
        max: maxUseEnergy,
        min: minUseEnergy,
        interval: useEnergyInterval,
      },
      {
        type: 'value',
        name: '产量',
        axisLabel: {
          formatter: '{value}',
        },
        max: productionMax,
        min: productionMin,
        interval: productionInterVal,
      },
    ],
  };

  return {
    ...commonOption,
    toolbox: {
      width: 200,
      height: 100,
      top: 0,
      right: 20,
      itemSize: 20, // 设置图标大小
      feature: {
        brush: {
          type: ['lineX', 'clear'], // 选择工具类型
          title: {
            lineX: '横向选择',
            clear: '清除',
          },
        },
      },
    },
    brush: {
      toolbox: ['lineX', 'clear'],
      xAxisIndex: 0,
    },
    // color: ['#16DD8E', ...(productionChartsColorMap ?? []), '#59D744', '#646464'],
    legend: {
      top: 6,
      right: resolution === DateType.MIN ? 266 : 100,
      itemWidth: 12,
      itemHeight: 12,
      data: [
        {
          name: '用能',
          icon: 'rect',
          itemStyle: {
            color: '#16DD8E', // 只改图例颜色
          },
        },
        ...(productionListData ?? []).map((i, index) => ({
          name: i.name,
          icon: 'rect',
          itemStyle: {
            color: productionChartsColors[index % productionChartsColors.length], // 只改图例颜色
          },
        })),
      ],
    },

    graphic: resolution === DateType.MIN && [
      {
        type: 'group',
        top: 10,
        right: 190,
        children: [
          {
            type: 'rect',
            shape: { width: 16, height: 8 },
            style: { fill: '#59D744' },
          },
          {
            type: 'text',
            left: 19,
            top: -2,
            style: {
              text: '生产时段',
              fill: 'rgba(255,255,255,0.85)',
              font: '12px sans-serif',
            },
          },
        ],
      },
      {
        type: 'group',
        top: 10,
        right: 100, // 位置可调
        children: [
          {
            type: 'rect',
            shape: { width: 16, height: 8 },
            style: { fill: '#646464' },
          },
          {
            type: 'text',
            left: 19,
            top: -2,
            style: {
              text: '非生产时段',
              fill: 'rgba(255,255,255,0.85)',
              font: '12px sans-serif',
            },
          },
        ],
      },
    ],

    series: [
      {
        type: 'line',
        data: [],
        markArea: {
          z: 10,
          data: rangeMarkArea(chartData?.rangeList),
        },
      },
      {
        name: '用能',
        type: 'bar',
        z: 1,
        timeResolution: resolution,
        data: useEnergyData,
        unit: chartData?.costDetailChartList?.[0]?.costUnitName,
        itemStyle: {
          color: '#16DD8E', // 图形颜色
        },
      },
      ...(productionListData ?? []),
      {
        name: '单耗',
        type: 'line',
        z: 1,
        data: productionCostData,
        showSymbol: false, // 不显示拐点
        lineStyle: { opacity: 0 }, // 线透明
        itemStyle: { opacity: 0 }, // 拐点透明
        emphasis: { disabled: true }, // 鼠标悬浮也不高亮
        tooltip: { show: true }, // tooltip 保留显示
      },
    ],
  };
};

// x轴下方
const rangeMarkArea = (rangeList: V2ProductionCostGetProductionCostPostResponse['rangeList']) => {
  return rangeList?.map(sl => {
    return [
      {
        xAxis: dayjs(sl.startTime).valueOf(),
        label: {
          show: false,
          // width: 120 - 24,
          // height: 60 - 16,
          backgroundColor: 'rgba(0,0,0,0.8)',
          padding: [8, 12],
          zIndex: 99,
          z: 99,
          formatter: [
            `{time|${dayjs(sl.startTime).format('MM-DD HH:ss')} - ${dayjs(sl.endTime).format('MM-DD HH:ss')}}`,
            `{status|${sl.isProduction ? StatusDisplay[Status.NORMAL] : StatusDisplay[Status.NO_NORMAL]}}`,
            `{useEnergy|累计用量: ${keepFourOrLine(sl.totalCost)}${sl.costUnitName}}`,
            `{production|${sl.showProductionName}: ${keepFourOrLine(sl.production)}${sl.productionUnitName}}`,
            `{cost|单耗: ${keepFourOrLine(sl.productionCost)}${sl.productionCostUnitName}}`,
          ].join('\n\n'),

          rich: {
            time: {
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 17,
              fonWeight: 500,
            },
            status: {
              color: sl.isProduction ? '#59D744' : '#646464',
              lineHeight: 17,
            },
            useEnergy: {
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 17,
            },
            production: {
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 17,
            },
            cost: {
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 17,
            },
          },
        },
        emphasis: {
          label: {
            show: true,
            position: ['0%', '-600%'],
            zIndex: 99,
          },
        },
        blur: {
          label: {
            show: false,
          },
        },
        itemStyle: {
          color: sl.isProduction ? '#59D744' : '#646464',
        },
        y: '90%',
      },
      {
        xAxis: dayjs(sl.endTime).valueOf(),
        y: '92%',
      },
    ];
  });
};
